import React, { useState, useEffect, useRef, useCallback } from 'react';
import Select from 'react-select';
import axios from 'axios';
import Swal from 'sweetalert2';
import { useUser } from '../context/UserContext';
import { fireAudit } from '../utils/auditClient'; 

const Rental = () => {
  const today = new Date().toISOString().split('T')[0];
  const { userData, selectedCenter } = useUser();
  const userId = userData?.userId;
  const token = localStorage.getItem('auth-token');

  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    date: today,
    academy: null,
    sport: null,
    venueQr: '',
    cash: '',
    comments: '',
    coordinatorName: '',
    coordinatorContact: '',
    companyName: ''
  });

  const [academies, setAcademies] = useState([]);
  const [academyOptions, setAcademyOptions] = useState([]);
  const [sportsOptions, setSportsOptions] = useState([]);
  const [optionsLoaded, setOptionsLoaded] = useState(false); 

  const baselineRef = useRef(null);
  const initializedBaselineRef = useRef(false);

  const buildSubmitPayload = useCallback(() => {
    const v = formData.venueQr === '' ? 0 : parseInt(formData.venueQr, 10) || 0;
    const c = formData.cash === '' ? 0 : parseInt(formData.cash, 10) || 0;

    return {
      userId,
      academyId: formData.academy?.value ?? null,
      sportId: formData.sport?.value ?? null,
      tournamentDate: formData.date,
      amountReceived: v + c,
      venueQr: v,
      cash: c,
      details: formData.comments || '',
      name: (formData.coordinatorName || '').trim(),
      companyName: (formData.companyName || '').trim(),
      mobileNumber: formData.coordinatorContact || '',
      isEnabled: 1,
    };
  }, [formData, userId]);

  const redactForAudit = useCallback((payload) => {
    if (!payload) return payload;
    const copy = JSON.parse(JSON.stringify(payload));
    if (copy.mobileNumber) copy.mobileNumber = '********';
    if (copy.details) copy.details = '********';
    return copy;
  }, []);

  useEffect(() => {
    if (initializedBaselineRef.current) return;
    if (!optionsLoaded) return;
    baselineRef.current = buildSubmitPayload();
    initializedBaselineRef.current = true;
  }, [optionsLoaded, buildSubmitPayload]);


  const onlyDigits = (val) => (val === '' ? '' : /^\d+$/.test(val) ? val : null);
  const toInt = (val) => (val === '' ? 0 : parseInt(val, 10) || 0);

  useEffect(() => {
    const fetchAcademies = async () => {
      try {
        const response = await axios.get(process.env.REACT_APP_API_URL_ACADEMIES, {
          headers: {
            'Content-Type': 'application/json',
            'x-authorization': process.env.REACT_APP_AUTH_TOKEN,
          }
        });

        const fetched = response.data?.data?.academies || [];
        const filtered =
          userData?.userType === 'ACADEMY' && Array.isArray(userData.academyIds)
            ? fetched.filter(a => userData.academyIds.includes(a.id))
            : fetched;

        setAcademies(filtered);
        setAcademyOptions(filtered.map(a => ({ label: a.name, value: a.id })));
        setOptionsLoaded(true); 
      } catch (error) {
        console.error('Error fetching academies:', error);
      }
    };

    fetchAcademies();
  }, [userData?.userType, userData?.academyIds]);

  useEffect(() => {
    if (!selectedCenter || academies.length === 0) return;
    const selected = academies.find(a => a.id === Number(selectedCenter));
    if (!selected) return;

    const academyOpt = { label: selected.name, value: selected.id };
    setFormData(prev => ({ ...prev, academy: academyOpt }));

    const sports = (selected.sports || []).map(s => ({
      label: s.name,
      value: s.sport_id,
      image: s.sport_pic
    }));
    setSportsOptions(sports);
  }, [selectedCenter, academies]);

  const handleAcademyChange = (selectedOption) => {
    setFormData(prev => ({
      ...prev,
      academy: selectedOption,
      sport: null
    }));

    const selected = academies.find(a => a.id === selectedOption?.value);
    const sports = (selected?.sports || []).map(s => ({
      label: s.name,
      value: s.sport_id,
      image: s.sport_pic
    }));
    setSportsOptions(sports);

    baselineRef.current = null;
    initializedBaselineRef.current = false;
  };

  const handleSportChange = (selectedOption) => {
    setFormData(prev => ({ ...prev, sport: selectedOption }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'coordinatorContact') {
      const numeric = value.replace(/\D/g, '');
      setFormData(prev => ({ ...prev, [name]: numeric }));
      return;
    }

    setFormData(prev => ({ ...prev, [name]: value }));
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    const {
      date, academy, sport, venueQr, cash, comments,
      coordinatorName, coordinatorContact, companyName
    } = formData;

    if (!date) {
      Swal.fire({ icon: 'error', title: 'Date Missing!', text: 'Please select a date.', toast: true, position: 'top-end', showConfirmButton: false, timer: 3000 });
      return;
    }
    if (!academy) {
      Swal.fire({ icon: 'error', title: 'Academy Not Selected!', text: 'Please select an academy.', toast: true, position: 'top-end', showConfirmButton: false, timer: 3000 });
      return;
    }
    if (!sport) {
      Swal.fire({ icon: 'error', title: 'Sport Not Selected!', text: 'Please select a sport.', toast: true, position: 'top-end', showConfirmButton: false, timer: 3000 });
      return;
    }

    const v = toInt(String(venueQr));
    const c = toInt(String(cash));
    if (v + c <= 0) {
      Swal.fire({ icon: 'error', title: 'Payment Missing!', text: 'Enter an amount (Venue QR or Cash) greater than 0.', toast: true, position: 'top-end', showConfirmButton: false, timer: 3000 });
      return;
    }

    if (!coordinatorName.trim()) {
      Swal.fire({ icon: 'error', title: 'Coordinator Name Missing!', text: 'Please enter the coordinator name.', toast: true, position: 'top-end', showConfirmButton: false, timer: 3000 });
      return;
    }

    if (!coordinatorContact.trim() || coordinatorContact.length !== 10) {
      Swal.fire({ icon: 'error', title: 'Invalid Contact Number!', text: 'Coordinator contact number must be exactly 10 digits.', toast: true, position: 'top-end', showConfirmButton: false, timer: 3000 });
      return;
    }

    const payload = {
      userId: userId,
      academyId: academy.value,
      sportId: sport.value,
      amountReceived: v + c,
      venueQr: v,
      cash: c,
      details: comments,
      tournamentDate: date,
      isEnabled: 1,
      name: coordinatorName.trim(),
      companyName: companyName.trim(),
      mobileNumber: coordinatorContact,
    };

    const newPayload = buildSubmitPayload();
    const oldPayload = baselineRef.current || {};

    try {
      setIsSubmitting(true);

      const response = await axios.post(
        process.env.REACT_APP_API_URL_TOURNAMENT,
        payload,
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          }
        }
      );

      const ok = response?.data?.success ?? true;
      if (ok) {
        Swal.fire({
          icon: 'success',
          title: 'Success!',
          text: 'Tournament entry added successfully.',
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 3000,
        });

        const affected_id =
          response?.data?.data?.id ??
          response?.data?.id ??
          `${academy.value}-${sport.value}-${date}`;

        fireAudit({
          entity: 'tournament',
          affected_id,
          action: 'create',
          businessApi: process.env.REACT_APP_API_URL_TOURNAMENT,
          businessMethod: 'POST',
          oldPayload: redactForAudit(oldPayload),
          newPayload: redactForAudit(newPayload),
          section: 'Centre Management',
          subSection: 'Walkin Sales(tournament)',
          extra: { page: 'Tournament', academyId: academy.value, sportId: sport.value, date },
        });

        baselineRef.current = newPayload;

        setFormData(prev => ({
          ...prev,
          venueQr: '',
          cash: '',
          comments: '',
          coordinatorName: '',
          coordinatorContact: '',
          companyName: ''
        }));
      } else {
        throw new Error(response?.data?.message || 'Failed to submit');
      }
    } catch (error) {
      console.error('API Error:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: error?.response?.data?.message || 'Failed to submit rental entry.',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div style={{ margin: 'auto' }}>
      <form onSubmit={handleSubmit}>
        {/* Date */}
        <div className="floating-select-form" style={{ width: '98%' }}>
          <input
            type="date"
            name="date"
            value={formData.date}
            onChange={handleChange}
            className={`walkin-form ${formData.date ? 'has-value' : ''}`}
            onFocus={(e) => e.target.showPicker?.()}
          />
          {formData.date && <label htmlFor="date">Date</label>}
        </div>

        {/* Academy */}
        <div className="floating-select-form" style={{ width: '100%' }}>
          <Select
            name="academy"
            options={academyOptions}
            value={formData.academy}
            onChange={handleAcademyChange}
            placeholder="Select Academy"
            className={`walkin-form-dropdown ${formData.academy ? 'has-value' : ''}`}
            classNamePrefix="walkin-select"
            isClearable
            styles={{
              control: (p) => ({ ...p, padding: '5px' }),
              placeholder: (p) => ({ ...p, color: 'black' }),
            }}
          />
          {formData.academy && <label htmlFor="academy">Academy</label>}
        </div>

        {/* Sport */}
        <div className="floating-select-form" style={{ width: '100%' }}>
          <Select
            name="sport"
            options={sportsOptions}
            value={formData.sport}
            onChange={handleSportChange}
            placeholder="Select Sport"
            className={`walkin-form-dropdown ${formData.sport ? 'has-value' : ''}`}
            classNamePrefix="walkin-select"
            isClearable
            styles={{
              control: (p) => ({ ...p, padding: '5px' }),
              placeholder: (p) => ({ ...p, color: 'black' }),
            }}
          />
          {formData.sport && <label htmlFor="sport">Sport</label>}
        </div>

        {/* Coordinator Name */}
        <div className="floating-select-form" style={{ width: '98%' }}>
          <input
            type="text"
            name="coordinatorName"
            value={formData.coordinatorName}
            onChange={handleChange}
            className={`walkin-form ${formData.coordinatorName ? 'has-value' : ''}`}
            placeholder="Coordinator Name"
            autoComplete="off"
          />
          {formData.coordinatorName && <label htmlFor="coordinatorName">Coordinator Name</label>}
        </div>

        {/* Coordinator Contact */}
        <div className="floating-select-form" style={{ width: '98%' }}>
          <input
            type="tel"
            name="coordinatorContact"
            value={formData.coordinatorContact}
            onChange={handleChange}
            className={`walkin-form ${formData.coordinatorContact ? 'has-value' : ''}`}
            placeholder="Coordinator Contact Number"
            inputMode="numeric"
            pattern="\d{10}"
            maxLength={10}
            autoComplete="off"
          />
          {formData.coordinatorContact && <label htmlFor="coordinatorContact">Coordinator Contact</label>}
        </div>

        {/* Company Name (optional) */}
        <div className="floating-select-form" style={{ width: '98%' }}>
          <input
            type="text"
            name="companyName"
            value={formData.companyName}
            onChange={handleChange}
            className={`walkin-form ${formData.companyName ? 'has-value' : ''}`}
            placeholder="Company Name (optional)"
            autoComplete="off"
          />
          {formData.companyName && <label htmlFor="companyName">Company Name</label>}
        </div>

        {/* Amounts */}
        <div>
          <label style={{ margin: 0 }}>Amount Received:</label>

          <div className="floating-select-form" style={{ width: '98%' }}>
            <input
              type="text"
              name="venueQr"
              value={formData.venueQr === 0 ? '0' : formData.venueQr || ''}
              onChange={(e) => {
                const v = onlyDigits(e.target.value);
                if (v !== null) setFormData(prev => ({ ...prev, venueQr: v === '' ? '' : parseInt(v, 10) }));
              }}
              className={`walkin-form ${formData.venueQr !== '' ? 'has-value' : ''}`}
              style={{ padding: '12px 10px' }}
              placeholder="Venue QR (Amount in ₹)"
              inputMode="numeric"
            />
            {formData.venueQr !== '' && <label htmlFor="venueQr">Venue QR</label>}
          </div>

          <div className="floating-select-form" style={{ width: '98%' }}>
            <input
              type="text"
              name="cash"
              value={formData.cash === 0 ? '0' : formData.cash || ''}
              onChange={(e) => {
                const v = onlyDigits(e.target.value);
                if (v !== null) setFormData(prev => ({ ...prev, cash: v === '' ? '' : parseInt(v, 10) }));
              }}
              className={`walkin-form ${formData.cash !== '' ? 'has-value' : ''}`}
              style={{ padding: '12px 10px' }}
              placeholder="Cash (Amount in ₹)"
              inputMode="numeric"
            />
            {formData.cash !== '' && <label htmlFor="cash">Cash</label>}
          </div>
        </div>

        {/* Comments */}
        <div className="floating-select-form" style={{ width: '98%' }}>
          <textarea
            name="comments"
            value={formData.comments}
            onChange={handleChange}
            className={`walkin-form ${formData.comments ? 'has-value' : ''}`}
            style={{ padding: '12px 10px' }}
            placeholder="Comments (optional)"
            maxLength={300}
          />
          {formData.comments && <label htmlFor="comments">Comments</label>}
        </div>

        <button className="submit-button" type="submit" disabled={isSubmitting}>
          {isSubmitting ? 'Submitting…' : 'Submit'}
        </button>
      </form>
    </div>
  );
};

export default Rental;
